import React, { useState, useCallback, useEffect, Fragment } from "react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import Dialog from "../dialog";
import CloseIcon from "../../icons/closeIcon";
import DateInput from "../dateInput";
import SearchDropdown from "../searchDropdown";
import LoadingButton from "../loadingButton";
import ErrorMessage from "../../components/errorMessage";
import FileUpload from "../fileInput";
import { dataUrlToFile } from "../../utils/dataUrlToFile";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import styles from "./style.module.css";
import formStyles from "../form/style.module.css";
import { addDays } from "date-fns";
const schema = {
  type: "object",
  required: ["date", "description"],
  properties: {
    date: {
      title: "Date",
      type: "string",
    },
    mileage: {
      title: "Mileage",
      type: "number",
    },
    category: {
      title: "Category",
      type: "string",
      options: [],
      placeholder: "Select Category",
      customClass: styles.category_dropdown,
      default: JSON.stringify({
        value: 'na',
        title: 'N/A - Non Consumable',
      }),
    },
    description: {
      title: "Description",
      type: "string",
    },
    source: {
      title: "Source",
      type: "string",
    },
    cost: {
      title: "Cost",
      type: "number",
    },
    files: {
      title: "Attachment",
      type: "string",
    },
  },
};

const uiSchema = {
  "ui:classNames": formStyles.formInner,
  "ui:rootFieldId": "add_service",
  date: {
    "ui:widget": DateInput,
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "Enter Date",
    "ui:options": {
      dateFormat: "MM-dd-yyyy",
      maxDate: addDays(new Date(), 0),
    },
  },
  mileage: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
    "ui:placeholder": "Enter Mileage",
  },
  category: {
    "ui:widget": SearchDropdown,
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
    "ui:options": {
      label: false,
      tooltipText:
        <Fragment>For non-consumables such as repairs or upgrades, select <strong>N/A Not Consumable</strong></Fragment>,
      position: ["right"],
      transform: { top: -15, left: 1 },
    },
  },
  description: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
    "ui:placeholder": "Enter Description",
  },
  source: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
    "ui:placeholder": "Enter Source",
  },
  cost: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
    "ui:placeholder": "Enter Cost",
  },
  files: {
    "ui:widget": FileUpload,
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_6}`,
    "ui:placeholder": "Upload Attachment",
    "ui:options": {
      label: false,
      tooltipText:
        <Fragment>Upload files such as receipts or photos for reference</Fragment>,
      position: ["right"],
      transform: { top: -15, left: 1 },
    },
  },
  "ui:submitButtonOptions": {
    props: {
      className: formStyles.form_btn,
    },
    submitText: "Add",
  },
};

const widgets = {
  date: DateInput,
  search_dropdown: SearchDropdown,
  files: FileUpload,
};

const AddServiceModal = ({
  title = "Add New Service",
  buttonTitle = "Add",
  serviceData = {},
  open,
  onClose,
  onAddService
}) => {
  const axiosPrivate = useAxiosPrivate();
  const [formData, setFormData] = useState({});
  const [newSchema, setNewSchema] = useState(schema);
  const [id, setID] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onFormSubmit = (formData) => {
    setFormData(formData);
    let tempData = { ...formData };
    if (tempData.files) {
      tempData.files = dataUrlToFile(tempData.files);
    }
    if (tempData.category) {
      let category = JSON.parse(tempData.category);
      if (category.value === "na") {
        delete tempData.category;
      } else {
        tempData.category = category.value;
      }
    }
    if (id) {
      handleUpdateService(tempData);
    } else {
      handleAddService(tempData);
    }
  };

  const handleClose = (changePage = false) => {
    setFormData({});
    onClose(changePage);
  };

  const handleAddService = useCallback(async (data) => {
    setLoading(true);
    setErrorMessage("");
    try {
      let url = `services/add`;
      let options = {
        method: "POST",
        url,
        data,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            handleClose(true);
            onAddService && onAddService('add');
          } else {
            handleClose();
          }
          setLoading(false);
        })
        .catch((err) => {
          if (err.response) {
            handleClose();
            setLoading(false);
            setErrorMessage(err.response.data.message);
          }
        });
    } catch (error) {
      handleClose();
      // console.error('error', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateService = useCallback(
    async (data) => {
      setLoading(true);
      setErrorMessage("");
      try {
        let url = `services/${id}`;
        let options = {
          method: "PUT",
          url,
          data,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 1) {
              handleClose(true);
              onAddService && onAddService('update');
            } else {
              handleClose();
              // console.error('err.res', response);
            }
            setLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              handleClose();
              setLoading(false);
              setErrorMessage(err.response.data.message);
            }
          });
      } catch (error) {
        // console.error('error', error);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  const fetchConsumables = useCallback(async () => {
    try {
      let url = `consumables/?page=1&limit=100`;
      let options = {
        method: "GET",
        url,
      };
      await axiosPrivate(options)
        .then((response) => {
          if (response.data.status === 1) {
            const tempSchema = { ...schema };
            const consumables = response.data.consumables;
            const consumableOptions = consumables
              .filter((item) => item.status === "active")
              .map((item) => ({
                value: item._id,
                title: item.name,
              }));

            tempSchema.properties.category.options = [
              {
                value: "na",
                title: "N/A - Non Consumable",
              },
              ...consumableOptions,
            ];
            setNewSchema(tempSchema);
          }
        })
        .catch((err) => {
          if (err.response) {
            // console.error('err.res', err.response.data);
          }
        });
    } catch (error) {
      // console.error('error', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (serviceData._id) {
      let tempData = { ...serviceData };
      delete tempData._id;
      setID(serviceData._id);
      Object.keys(tempData).forEach(key => {
        if (tempData[key] === null || tempData[key] === undefined) {
          delete tempData[key];
        }
      });
      setFormData(tempData);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceData]);

  useEffect(() => {
    fetchConsumables();
  }, [fetchConsumables]);

  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="680px">
      <div className={styles.add_service_modal}>
        <div className={styles.add_service_modal_header}>
          <div className={styles.add_service_modal_header_title}>{title}</div>
          <button
            type="button"
            className={styles.add_service_modal_header_close}
            onClick={() => handleClose(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.add_service_modal_content}>
          <Form
            className={formStyles.form}
            schema={newSchema}
            uiSchema={uiSchema}
            widgets={widgets}
            formData={formData}
            validator={validator}
            showErrorList={false}
            omitExtraData={true}
            liveValidate={false}
            onSubmit={({ formData }) => {
              onFormSubmit(formData);
            }}
          >
            <div className={formStyles.form_actions}>
              <LoadingButton
                type="submit"
                loading={loading}
                title={buttonTitle}
              />
            </div>
          </Form>
          {errorMessage && <ErrorMessage message={errorMessage} mt />}
        </div>
      </div>
    </Dialog>
  );
};

export default AddServiceModal;
